import React, { useState } from "react";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  ListActions,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleList,
  TextField,
} from "react-admin";
import { Box, Tab, Tabs, useMediaQuery } from "@mui/material";
import { ACCOMMODATIONS, DormitoryAccommodationStatuses, DormitoryServicePath, MOBILE_BREAKPOINT, STUDENT_CATEGORIES } from "utils/constants";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { DormitoryOutstandingDebtChoices } from "utils/choices";
import { administrationRequests } from "api/administrationApi";
import { dormitoryRequests } from "api/dormitoryApi";
import DatagridActions from "../Partials/DatagridActions";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import CheckOutDialog from "../Accommodations/CheckOutDialog";
import ConfirmMoveDialog from "../Accommodations/ConfirmMoveDialog";
import CancelMoveDialog from "../Accommodations/CancelMoveDialog";
import ApplicationDetailsModal from "../Accommodations/ApplicationDetailsModal";
import CreateOrderButton from "../AccommodationOrders/CreateOrderButton";
import StudentCategoryTabs from "../Partials/StudentCategoryTabs";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import { accommodationRoomLabel, hasPendingMove } from "../Partials/roomLabels";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import OutstandingDebtField from "./OutstandingDebtField";

// Name and PIN cannot be filtered server-side: the tenant's identity is resolved from AccountService
// after the page is read, not stored in this service's DB. Building is the user-facing list filter;
// status and external-resident category are fixed filters applied by the screen below.
const listFilters = [
  <ReferenceInput
    key="buildingId"
    source="buildingId"
    reference={dormitoryRequests.getBuildingsSelect()}
    alwaysOn
  >
    <DormitoryAutocompleteInput
      optionText="name"
      optionValue="id"
      label="Блок"
      emptyText="Всички"
    />
  </ReferenceInput>,
  <SelectInput
    key="hasOutstandingDebts"
    source="hasOutstandingDebts"
    label="Задължения"
    choices={DormitoryOutstandingDebtChoices}
    emptyText="Всички"
    alwaysOn
  />,
];

const renderStudentFullName = record => record?.studentFullName || "—";

const renderPin = record => record?.personId || "—";

// Shows "current → destination" while a move is approved but not yet confirmed, and the plain
// current room otherwise (covers desktop Datagrid and mobile SimpleList through this one helper).
const renderRoom = record => accommodationRoomLabel(record);

const renderPeriod = record =>
  `${formatDateOnlyBg(record?.movedInAt)} – ${formatDateOnlyBg(record?.movedOutAt)}`;

const renderRowActions = (record, isExternal) => {
  const isActive = record?.status === DormitoryAccommodationStatuses.Active;
  return (
    <>
      <Box>
        <ApplicationDetailsModal record={record} isExternal={isExternal} isAccommodatedView={true} />
      </Box>
      <Box>
        {isActive && (
          <SecuredComponent permission={permissions.canManageDormitoryAccommodationOrders}>
            <CreateOrderButton accommodationId={record?.id} />
          </SecuredComponent>
        )}
      </Box>
      {isActive && hasPendingMove(record) && (
        <SecuredComponent permission={permissions.canManageDormitoryAccommodations}>
          <Box>
            <ConfirmMoveDialog record={record} />
          </Box>
          <Box>
            <CancelMoveDialog record={record} />
          </Box>
        </SecuredComponent>
      )}
      {isActive && (
        <SecuredComponent permission={permissions.canManageDormitoryAccommodations}>
          <Box>
            <CheckOutDialog record={record} />
          </Box>
        </SecuredComponent>
      )}
    </>
  );
};

const ListAccommodatedStudents = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [studentCategory, setStudentCategory] = useState(STUDENT_CATEGORIES.INTERNAL);
  const [accommodationStatus, setAccommodationStatus] = useState(DormitoryAccommodationStatuses.Active);
  const isExternal = studentCategory === STUDENT_CATEGORIES.EXTERNAL;
  const isActive = accommodationStatus === DormitoryAccommodationStatuses.Active;
  return (
    <>
      <StudentCategoryTabs value={studentCategory} onChange={setStudentCategory} />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={accommodationStatus} onChange={(_, value) => setAccommodationStatus(value)}>
          <Tab value={DormitoryAccommodationStatuses.Active} label="Настанени" />
          <Tab value={DormitoryAccommodationStatuses.Left} label="Напуснали" />
        </Tabs>
      </Box>

      {/* 2. Active accommodated students list */}
      <List
        key={`${studentCategory}-${accommodationStatus}`}
        resource={`${DormitoryServicePath}${ACCOMMODATIONS}`}
        title="Настанени студенти"
        exporter={false}
        filters={listFilters}
        filter={{
          status: accommodationStatus,
          isExternal,
        }}
        actions={<ListActions hasCreate={false} />}
      >
        {isMobile ? (
          <SimpleList
            linkType={false}
            sx={settings.style.mobileList}
            primaryText={record => (
              <MobileListRow
                fields={[
                  { label: "Имена", value: renderStudentFullName(record) },
                ]}
              />
            )}
            secondaryText={record => (
              <div>
                {!isExternal && (
                  <MobileListRow fields={[{ label: "Факултетен №", value: record?.facultyNumber || "—" }]} />
                )}
                <MobileListRow fields={[{ label: "ЕГН", value: renderPin(record) }]} />
                {isExternal && (
                  <MobileListRow
                    fields={[
                      {
                        label: "Университет",
                        value: (
                          <ReferenceField
                            source="originUniversityId"
                            reference={administrationRequests.getUniversitiesSelect()}
                            link={false}
                            emptyText="—"
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                )}
                <MobileListRow fields={[{ label: "Стая", value: renderRoom(record) }]} />
                <MobileListRow fields={[{ label: "Задължения", value: <OutstandingDebtField /> }]} />
                <MobileListRow
                  fields={[
                    {
                      label: "Дата на настаняване",
                      value: isActive ? formatDateOnlyBg(record?.movedInAt) : renderPeriod(record),
                    },
                  ]}
                />
              </div>
            )}
            tertiaryText={record => renderRowActions(record, isExternal)}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
          >
            {!isExternal && (
              <TextField
                source="facultyNumber"
                label="Факултетен №"
                emptyText="—"
                sortable={false}
              />
            )}
            <FunctionField
              source="studentFullName"
              label="Имена"
              render={renderStudentFullName}
              sortable={false}
            />
            <FunctionField
              source="personId"
              label="ЕГН"
              render={renderPin}
              sortable={false}
            />
            {isExternal && (
              <ReferenceField
                source="originUniversityId"
                reference={administrationRequests.getUniversitiesSelect()}
                label="Университет"
                link={false}
                emptyText="—"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
            )}
            <FunctionField
              source="room"
              label="Стая"
              render={renderRoom}
              sortable={false}
            />
            <FunctionField
              source="hasOutstandingDebts"
              label="Задължения"
              sortable={false}
              render={() => <OutstandingDebtField />}
            />
            {isActive ? (
              <DateField
                source="movedInAt"
                label="Дата на настаняване"
                locales="bg-BG"
                sortable={false}
              />
            ) : (
              <FunctionField
                source="accommodationPeriod"
                label="Период на настаняване"
                render={renderPeriod}
                sortable={false}
              />
            )}
            <FunctionField
              source="actions"
              label={false}
              sortable={false}
              render={record => (
                <DatagridActions>{renderRowActions(record, isExternal)}</DatagridActions>
              )}
            />
          </Datagrid>
        )}
      </List>
    </>
  );
};

export default ListAccommodatedStudents;
